import React from 'react';
import { Mic } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/blocks/PageHero';
import { TextImageSection } from '../components/blocks/TextImageSection';
import { CheckList } from '../components/blocks/CheckList';
import { StatementList } from '../components/blocks/StatementList';
import { SectionHeading } from '../components/blocks/SectionHeading';
import { FaqSection } from '../components/blocks/FaqSection';
import { CtaBanner } from '../components/blocks/CtaBanner';
import { SectionDivider, SURFACE } from '../components/blocks/SectionDivider';
import { useSeo } from '../lib/useSeo';
import { faqSchema } from '../lib/schema';
import { PRACTITIONER, SITE } from '../lib/siteConfig';

/**
 * PAGE: /meet-milangela
 * WORDPRESS: page template `page-meet-milangela.php`
 *
 * Blocks: hero → text_image → prose (approach) → text + checklist
 *         (credentials) → statement_list (beliefs) + media panel →
 *         faq → cta_banner
 *
 * This page carries the Person entity that the rest of the site references by
 * @id — the strongest E-E-A-T and AI-attribution signal on the whole site.
 */

const HERO_IMAGE = 'https://images.unsplash.com/photo-1499002238440-d264edd596ec?auto=format&fit=crop&q=80&w=2000';
const PORTRAIT_IMAGE = PRACTITIONER.image;

/** ACF: repeater `beliefs` */
const beliefs = ['I believe in the wisdom of the body.', 'I believe in the healing power of presence.', 'I believe that nature teaches us patience, resilience and trust.', 'I believe that true transformation cannot be forced — it unfolds when we create the right conditions.', 'I believe that every person deserves to feel seen, heard and accepted.'];

/** ACF: repeater `page_faqs` — "who is…" queries are prime AI-answer territory. */
const faqs = [{
  question: 'Who is Branka Androjna?',
  answer: `Branka Androjna, known as Milangela, is a certified Biofield Tuning practitioner and meditation guide, and the founder of ${SITE.name}. Her professional path began in education as an English teacher and translator before a turning point through illness led her to explore the relationship between mind, body and our natural capacity for healing.`
}, {
  question: 'What qualifications does Branka hold?',
  answer: `${PRACTITIONER.credentials.join('; ')}.`
}, {
  question: 'Is Branka a therapist or doctor?',
  answer: 'No. Branka is not a psychologist, therapist or medical doctor and does not offer diagnoses or treatment. Her work is complementary and presence-based, supporting relaxation, reflection and self-connection alongside — never instead of — professional medical or psychological care.'
}];
export function Story() {
  useSeo({
    title: 'Meet Milangela',
    description: `Meet Branka Androjna — certified Biofield Tuning practitioner, meditation guide and the heart behind ${SITE.name}. A path shaped by presence, compassion and trust.`,
    path: '/meet-milangela',
    image: PORTRAIT_IMAGE,
    type: 'profile',
    breadcrumbs: [{
      name: 'Meet Milangela',
      path: '/meet-milangela'
    }],
    schema: [faqSchema(faqs), {
      '@type': 'AboutPage',
      name: `About ${PRACTITIONER.name}`,
      url: `${SITE.url}/meet-milangela`,
      mainEntity: {
        '@id': `${SITE.url}/#branka`
      }
    }]
  });
  return <div className="min-h-screen bg-cream" data-id="element-614" data-mp="18mxq2ess2frc">
      <Header data-id="element-615" data-mp="18lmp0mqpvm3x" />

      <main data-id="element-616" data-mp="18kbhqcy2bw82">
        <PageHero eyebrow="Meet Milangela" heading="The heart behind Milangela's Soul Garden." body="I believe that every person carries within them the ability to heal, grow and reconnect with themselves. My role is not to change who you are, but to create a space where that process can unfold with presence, compassion and trust." image={HERO_IMAGE} imageAlt="A peaceful lavender field at dusk" dividerFill={SURFACE.cream} data-id="element-617" data-mp="13nbd86gv31j2" />

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-618" data-mp="1745v9jax5byw">
          <TextImageSection eyebrow="My journey" heading="My journey began with a question." image={PORTRAIT_IMAGE} imageAlt="Branka Androjna holding pink flowers in the garden" imageShape="circle" imagePosition="left" paragraphs={[{
          text: 'My professional path began in education. As an English teacher, translator and lifelong learner, I spent decades working with people of different backgrounds, personalities and life stories. Those experiences taught me something that still shapes my work today: every person experiences the world differently, and every journey deserves to be met with understanding rather than judgement.'
        }, {
          text: 'Life, however, had its own lessons waiting for me.',
          style: 'lead'
        }, {
          text: 'A turning point came through illness. During a period when I was searching for strength and a way forward, I began exploring the deeper relationship between the mind, the body and our natural capacity for healing. What started as a personal search gradually became a lifelong path of learning, transformation and service.'
        }, {
          text: 'It was this journey that eventually gave birth to Milangela’s Soul Garden — not as a business idea, but as a place where others could also reconnect with themselves, find support and discover their own inner strength.'
        }]} data-id="element-619" data-mp="172mg5w1016yp" />
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.rose} data-id="element-620" data-mp="3p36tr2e77850" />

        <section className="bg-rose-50 px-6 pb-24 pt-8 md:pb-28" data-id="element-621" data-mp="3kalxlu7tkiyb">
          <div className="mx-auto max-w-6xl border-l-2 border-rose pl-6 sm:pl-10" data-id="element-622" data-mp="2wlb5t28g5ywb">
            <SectionHeading eyebrow="The way I choose to work" heading="Presence before everything else." accent="rose" data-id="element-623" data-mp="2wj89y1km5bjo" />

            <div className="mt-8 grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-id="element-624" data-mp="vik1xfdc26ej">
              <div className="font-serif text-2xl italic leading-snug text-charcoal" data-id="element-625" data-mp="10gvdbuja0oyv">
                <p data-id="element-626" data-mp="159xxxd62so2x">People often ask me what makes my work different.</p>
                <p className="mt-6" data-id="element-627" data-mp="19a8d9gfrcqug">The answer is simple.</p>
                <p className="mt-6 text-3xl text-rose-700" data-id="element-628" data-mp="199otdu6jrsds">
                  I don&apos;t believe in fixing people.
                </p>
                <p className="mt-3" data-id="element-629" data-mp="hj0ukosciz3a">
                  I believe in creating the right conditions for healing to
                  happen naturally.
                </p>
              </div>

              <div className="space-y-5 text-base font-light leading-relaxed text-charcoal-50" data-id="element-630" data-mp="qjz41mljzyt9">
                <p data-id="element-631" data-mp="1x02ra1btclga">
                  Whether through sound, guided meditation or compassionate
                  conversation, my intention is always the same: to offer a
                  calm, respectful and safe space where you can slow down,
                  listen to yourself again and reconnect with what already
                  exists within you.
                </p>
                <p data-id="element-632" data-mp="2762lgfre23j8">
                  I believe that the body carries its own intelligence. When we
                  learn to listen instead of constantly trying to control,
                  balance begins to return in its own time.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-633" data-mp="1odhx9bmmwmlr">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-634" data-mp="1obx49qfyxbq0">
            <SectionHeading eyebrow="A lifelong practice" heading="My Journey of Learning" intro={<>
                  <p data-id="element-636" data-mp="3dsprktkvksxt">
                    Learning has always been part of my path, that&apos;s why my
                    work today is built on both personal experience and
                    continuous professional development.
                  </p>
                  <p data-id="element-637" data-mp="3t1p60yvhnxyg">
                    Over the years I have studied with internationally
                    recognised teachers in the fields of meditation, energy
                    medicine and sound therapy, gradually integrating these
                    approaches into my own way of working.
                  </p>
                </>} data-id="element-635" data-mp="1jex57zhjd98e" />

            <CheckList lead="Education & Certifications" items={PRACTITIONER.credentials} data-id="element-638" data-mp="39atulrxozo0w" />
          </div>
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.rose} data-id="element-639" data-mp="30bglj0dhd1gp" />

        <section className="bg-rose-50 px-6 pb-24 pt-8 md:pb-28" data-id="element-640" data-mp="30d9uimbkfxrv">
          <div className="mx-auto grid max-w-6xl gap-16 lg:grid-cols-[1fr_0.9fr] lg:items-start lg:gap-20" data-id="element-641" data-mp="35ajtaskuhlsd">
            <div data-id="element-642" data-mp="ykbx0d6ugh0l">
              <SectionHeading eyebrow="What I believe" heading="Get to know me more." accent="rose" data-id="element-643" data-mp="yhpq8frs25jf" />
              <StatementList statements={beliefs} className="mt-8" data-id="element-644" data-mp="1dndf1bj4yrvh" />
              <p className="mt-9 font-serif text-2xl italic text-charcoal" data-id="element-645" data-mp="18o0q8l8pfaq9">
                And above all,
              </p>
              <p className="mt-3 font-serif text-3xl italic leading-snug text-rose-700" data-id="element-646" data-mp="1mfp2qpmv817r">
                I believe there is more strength within each of us than we often
                realise.
              </p>
            </div>

            <aside className="border border-rose/50 bg-rose-100 p-7 sm:p-9" data-id="element-647" data-mp="1wf66v2mgy71m">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-rose/60 text-rose-700" data-id="element-648" data-mp="1rdgsbd01fb8s">
                <Mic size={20} strokeWidth={1.35} aria-hidden="true" data-id="element-649" data-mp="25bngg1f6pwv9" />
              </div>
              <p className="mt-6 font-serif text-2xl italic leading-snug text-charcoal" data-id="element-650" data-mp="21cxzxzyunz7e">
                Listen to my podcast — Why Milangela&apos;s Soul Garden?
              </p>
              <a href="https://creators.spotify.com/pod/profile/milangela/" target="_blank" rel="noopener noreferrer" className="group mt-7 flex items-center gap-4 bg-[#6B5B82] p-4 text-left text-white shadow-soft transition-colors hover:bg-[#5e4f73] focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 focus:ring-offset-rose-50" aria-label="Listen to episode 1: Why Milangela's Soul Garden (opens in a new tab)" data-id="element-651" data-mp="6zpxpn8jolbq">
                <img src={SITE.logo} alt="" aria-hidden="true" loading="lazy" decoding="async" width={80} height={80} className="h-20 w-20 shrink-0 rounded-full object-cover" data-id="element-652" data-mp="37c7gasntjugv" />
                <span className="min-w-0 flex-1" data-id="element-653" data-mp="3q1dz0fom4m2o">
                  <span className="block font-serif text-xl leading-tight" data-id="element-654" data-mp="3q2yx2fa4yn5a">
                    EP1: Why Milangela&apos;s Soul Garden
                  </span>
                  <span className="mt-2 block text-xs text-white/75" data-id="element-655" data-mp="3smdgq8als2av">
                    {SITE.name}
                  </span>
                </span>
              </a>
            </aside>
          </div>
        </section>

        <section className="bg-cream px-6 py-20 md:py-24" data-id="element-656" data-mp="6ciwsmedkfn4">
          <div className="mx-auto max-w-3xl" data-id="element-657" data-mp="bdq4klgipets">
            <FaqSection eyebrow="About Branka" heading="Common questions" faqs={faqs} data-id="element-658" data-mp="berprir8zhp8" />
          </div>
        </section>

        <CtaBanner heading="If my story resonates with you, perhaps this is where your own journey begins." body="Whether you are looking for greater balance, emotional clarity or simply a place where you can pause and breathe again, I would be honoured to walk alongside you." buttonLabel="Start Your Journey" buttonTo="/start-your-journey" dividerFrom={SURFACE.cream} data-id="element-659" data-mp="kctpzw35q6bg" />
      </main>

      <Footer data-id="element-660" data-mp="1329zh8wtl940" />
    </div>;
}